import { useFiles } from '../../hooks/useFiles';
import { useState, useRef, useEffect } from 'react';
import useOnClickOutside from '@/hooks/useClickOutside';
import { FileListToolbar } from './components/FileListToolbar/FileListToolbar';
import { FileListTable } from './components/FileListTable/FileListTable';
import { FileListPagination } from './components/FileListPagination/FileListPagination';
import { FileListFilters } from './components/FileListFilters/FileListFilters';
import { useTour } from '@reactour/tour';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';

export const FileList = () => {
  const [openFilter, setOpenFilter] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const {
    table,
    isLoading,
    isFetching,
    error,
    refetch,
    downloadCsv,
    isDownloading,
    handleNextPage,
    handlePreviousPage,
    canPreviousPage,
    canNextPage,
    setSearchValue,
    searchValue,
    setOnlyMyDocs,
    onlyMyDocs,
    pageNb,
    pageSize,
    totalFiles,
    filters,
    handleToggleFilter,
    resetFilters,
    handleDragEnd,
    isDragEnabled,
  } = useFiles();

  // Tour logic
  const { setCurrentStep, currentStep, isOpen } = useTour();

  const { hasCompletedFilesTutorial } = useSelector(
    (state: RootState) => state.tutorial
  );

  useEffect(() => {
    if (isOpen) {
      if (currentStep === 3) {
        setOpenFilter(true);
      } else {
        setOpenFilter(false);
      }
    }
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (isOpen && openFilter) {
      setCurrentStep(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFilter]);

  useEffect(() => {
    if (isOpen) {
      if (
        currentStep === 3 &&
        filters.length === 1 &&
        filters[0] === 'isFavorite'
      ) {
        setCurrentStep(4);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (isOpen) {
      if (searchValue === 'remb_sante' && !hasCompletedFilesTutorial) {
        setCurrentStep(5);
      }
    }
  }, [isOpen, searchValue, setCurrentStep, hasCompletedFilesTutorial]);

  useOnClickOutside([filterRef, filterButtonRef], () => {
    if (openFilter && !isOpen) {
      setOpenFilter(false);
    }
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div>
      <FileListToolbar
        isLoading={isFetching}
        refetch={refetch}
        downloadCsv={downloadCsv}
        isDownloading={isDownloading}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onlyMyDocs={onlyMyDocs}
        setOnlyMyDocs={setOnlyMyDocs}
        filters={filters}
        handleToggleFilter={handleToggleFilter}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        filterButtonRef={filterButtonRef}
        table={table}
      />
      {openFilter && (
        <FileListFilters
          filters={filters}
          handleToggleFilter={handleToggleFilter}
          filterRef={filterRef}
          resetFilters={resetFilters}
        />
      )}

      <div className="relative">
        <FileListTable
          table={table}
          error={error}
          onDragEnd={handleDragEnd}
          isDragEnabled={isDragEnabled}
        />
      </div>

      <FileListPagination
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageNb={pageNb}
        pageSize={pageSize}
        totalFiles={totalFiles}
      />
    </div>
  );
};
